import { useMemo, useRef, useState } from 'react';

import { useQuery as useTanStackQuery } from '@tanstack/react-query';
import { t } from 'i18next';
import { AutoCompleteSelectEvent } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import { DropdownChangeEvent } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Panel, PanelToggleEvent } from 'primereact/panel';
import { SelectButton } from 'primereact/selectbutton';
import { useDispatch } from 'react-redux';

import { useAppSelector } from '@/app/hooks';
import { useLazySearchDrugsQuery } from '@/app/services/lookups';
import { DonorSchedulingActions } from '@/app/slices/donorScheduling';
import { addToast } from '@/app/slices/toast';
import {
  DrugsAutoComplete,
  ScheduleAutoComplete,
  StringMapDropdown,
} from '@/components';
import { ScheduleSelectionDialog } from '@/components/common/schedule-selection-dialog/ScheduleSelectionDialog';
import { Schedule } from '@/components/common/schedule-selection-dialog/types';
import {
  StringMapAttributeName,
  StringMapObjectTypeCode,
} from '@/components/common/string-map-dropdown';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { SELECTION_LIMIT } from '@/constants';
import { LOAD_DONOR_PARTNERS_OPTION_VALUES } from '@/enums';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { isOpenCombobox, useFormNav } from '@/hooks/useFormNav';
import { useAccessToken } from '@/services/auth';
import {
  previousSchedulesV3Options,
  type SoScheduleEditResponse,
} from '@/services/donor-scheduling';

import {
  CLINIC_RANCH_HAUL_IN_OPTIONS,
  TARGET_TIME_OPTIONS,
} from '../../../constants';
import {
  findExactDrugByName,
  getFolltropinDrugName,
  isFolltropinDefaultDrug,
  isNoStimSchedule,
} from '../../../util/fshDefaulting';
import {
  donorSchedulePatchFromSoSchedule,
  EditSoScheduleDialog,
  isSoScheduleLocked,
} from '../../edit-so-schedule-dialog';
import { PartnershipForm } from '../../partnership-form';
import {
  PartnershipTypeLabel,
  PartnershipTypeValue,
} from '../../partnership-form/constants';
import { TRUE_FALSE_SELECT_OPTIONS } from '../constants';

type DonorScheduleFormSchedulesProps = {
  className?: string;
  collapsed: boolean;
  viewOnly?: boolean;
  onCollapseChange: (collapsed: boolean) => void;
  unlockAll?: boolean | null;
  setUnlockAll?: (unlocked: boolean) => void;
  handleSoScheduleDialogYes?: () => void;
};

export const DonorScheduleFormSchedules = ({
  className = '',
  collapsed,
  viewOnly = false,
  onCollapseChange,
  unlockAll,
  setUnlockAll,
  handleSoScheduleDialogYes,
}: DonorScheduleFormSchedulesProps) => {
  const ref = useRef(null);
  const isScheduleMasterEnterEnabled = useFeatureFlag('DS_Master_Enter');

  const {
    ref: formNavRef,
    onKeyDownCapture,
    handleEnterKeyAdvance,
  } = useFormNav<HTMLDivElement>({
    selectors: [
      'input:not([type="hidden"]):not([disabled]):not([readonly])',
      'select:not([disabled])',
      '.p-dropdown:not(.p-disabled) input',
      '.p-dropdown:not(.p-disabled) input[role="combobox"]',
      '.p-dropdown:not(.p-disabled) .p-dropdown-label',
      '.p-autocomplete:not(.p-disabled) input',
      '.p-calendar:not(.p-disabled) input',
      '.p-inputmask:not(.p-disabled) input',
      '.p-inputnumber:not(.p-disabled) input',
      '.p-selectbutton:not(.p-disabled) .p-button:first-child',
      '.p-nucleus:not(.p-disabled) .p-button:first-child',
      'textarea:not([disabled]):not([readonly])',
      'button:not([disabled])[tabindex]',
    ],
    wrap: true,
    sortByTabIndex: true,
    allowDropdownEnterSelect: isScheduleMasterEnterEnabled,
  });

  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [showEditSoScheduleDialog, setShowEditSoScheduleDialog] =
    useState(false);
  const isUpdateSoSchedulesEnabled = useFeatureFlag('Update_So_Schedules');
  const [selectedSchedule, setSelectedSchedule] = useState<any>(null);

  const dispatch = useDispatch();
  const isScheduleSentEnabled = useFeatureFlag('DS_Schedule_Sent');
  const isRequiredFieldAsterisksEnabled = useFeatureFlag(
    'DS_Required_Field_Asterisks',
  );
  const scheduleSentTabOffset = isScheduleSentEnabled ? 1 : 0;
  const [searchDrugs] = useLazySearchDrugsQuery();
  const fshDrugs = useAppSelector((state) => state.lookups.fshDrugs);
  const { workingDonorSchedule } = useAppSelector(
    (state) => state.donorScheduling,
  );

  const hasSoSchedule = workingDonorSchedule?.soSchedule?.id != null;
  // The donor schedule date is the aspiration date.
  const isSoScheduleEditLocked = isSoScheduleLocked(workingDonorSchedule?.date);
  const isFieldLocked = hasSoSchedule && !unlockAll;
  const token = useAccessToken();

  const isDateGreaterThanOrEqualToToday = useMemo(() => {
    return (
      new Date(workingDonorSchedule?.date) >=
      new Date(new Date().setHours(0, 0, 0, 0))
    );
  }, [workingDonorSchedule?.date]);

  const { data: previousSchedulesV3 } = useTanStackQuery({
    ...previousSchedulesV3Options(token, {
      donorScheduleId: workingDonorSchedule?.id ?? '',
      donorId: workingDonorSchedule?.donor?.id ?? '',
      ownerId: workingDonorSchedule?.owner?.id ?? '',
    }),
    enabled:
      !!token &&
      !!workingDonorSchedule?.id &&
      !!workingDonorSchedule?.donor?.id &&
      !!workingDonorSchedule?.owner?.id,
  });

  const handleSoScheduleWarning = () => {
    if (isFieldLocked) {
      confirmDialog({
        message:
          'This donor has a SO Schedule. Changing the drug, target time, or schedule master will deactivate it. You will need to create a new SO Schedule. Do you want to proceed?',
        header: 'SO Schedule Warning',
        icon: 'pi pi-exclamation-triangle',
        accept: handleSoScheduleDialogYes,
        reject: handleSoScheduleDialogNo,
        acceptLabel: 'Yes',
        rejectLabel: 'No',
        acceptClassName: 'p-button-warning',
        rejectClassName: 'p-button-secondary',
      });
    } else {
      if (setUnlockAll) {
        setUnlockAll(false);
      }
    }
  };

  const handleSoScheduleDialogNo = () => {
    if (setUnlockAll) {
      setUnlockAll(false);
    }
  };

  // The API copies the changed SO fields to the donor schedule on save. Mirror them into
  // the open form so saving the donor schedule afterwards doesn't write the old values back.
  const handleSoScheduleSaved = (
    soSchedule: SoScheduleEditResponse,
    previous: SoScheduleEditResponse,
  ) => {
    dispatch({
      type: DonorSchedulingActions.UPDATE_WORKING_DONOR_SCHEDULE,
      payload: {
        ...workingDonorSchedule,
        ...donorSchedulePatchFromSoSchedule(soSchedule, previous),
        soSchedule: {
          ...workingDonorSchedule?.soSchedule,
          description: soSchedule.description,
        },
      },
    });
    dispatch({ type: DonorSchedulingActions.INCREMENT_REFETCH_TRIGGER });
  };

  const handleScheduleCancel = () => {
    setShowScheduleDialog(false);
    setSelectedSchedule(null);
  };

  const getCollapsedValue = () => {
    return workingDonorSchedule?.loadDonorPartners !==
      LOAD_DONOR_PARTNERS_OPTION_VALUES.YES
      ? true
      : isDateGreaterThanOrEqualToToday
        ? true
        : collapsed;
  };

  const handleDrug1Change = (e: AutoCompleteSelectEvent) => {
    dispatch({
      type: DonorSchedulingActions.UPDATE_FSH_DRUG_1,
      payload: {
        fshId: e.value.id,
        drug: e.value,
      },
    });
  };

  const handleDrug2Change = (e: AutoCompleteSelectEvent) => {
    dispatch({
      type: DonorSchedulingActions.UPDATE_FSH_DRUG_2,
      payload: {
        fsh2Id: e.value.id,
        drug2: e.value,
      },
    });
  };

  const handleTargetTimeChange = (e: DropdownChangeEvent) => {
    dispatch({
      type: DonorSchedulingActions.UPDATE_TARGET_TIME,
      payload: e.value,
    });
  };

  const renderLockIcon = () => {
    if (!hasSoSchedule) return null;

    return (
      <Button
        icon={`pi pi-${unlockAll ? 'lock-open' : 'lock'}`}
        className="p-button-text p-button-sm ml-2"
        onClick={handleSoScheduleWarning}
        title="Click to unlock fields (SO Schedule exists)"
      />
    );
  };

  return (
    <div
      className={`donor-schedule-form-schedules ${className}`.trim()}
      onKeyDownCapture={(e) => {
        if (e.key === 'Enter') {
          handleEnterKeyAdvance(e);
        } else {
          onKeyDownCapture(e);
        }
      }}
      ref={formNavRef}
    >
      <Panel
        className="column-span donor-schedule-partnership"
        data-pr-disabled={!workingDonorSchedule?.donor}
        ref={ref}
        header={`Partnership - ${workingDonorSchedule?.donor ? workingDonorSchedule?.donor?.name : 'Select a Donor'}`}
        toggleable
        collapsed={getCollapsedValue()}
        onToggle={(e: PanelToggleEvent) => onCollapseChange(e.value)}
      >
        <PartnershipForm.Root>
          <PartnershipForm.Tabs
            types={[
              {
                attributeValue: PartnershipTypeValue.DONOR_PARTNERSHIP_BILLING,
                value:
                  PartnershipTypeLabel[
                    PartnershipTypeValue.DONOR_PARTNERSHIP_BILLING
                  ],
              },
              {
                attributeValue: PartnershipTypeValue.SPECIAL_BILLING,
                value:
                  PartnershipTypeLabel[PartnershipTypeValue.SPECIAL_BILLING],
              },
            ]}
            viewOnly={viewOnly}
          />
        </PartnershipForm.Root>
      </Panel>

      <div className="grid w-full grid-cols-2 gap-6">
        <div className="label-input-group">
          <div className="flex w-full flex-row">
            <DrugsAutoComplete
              label={t('donorScheduling.fshDrug1')}
              defaultValue={workingDonorSchedule?.drug || undefined}
              field="name"
              disabled={viewOnly || isFieldLocked}
              onSelect={handleDrug1Change}
              className="w-full"
              tabIndex={1}
              forceNewSuggestionsRef
            />
            <div className="label-input-group">
              <label></label>
              {renderLockIcon()}
            </div>
          </div>
        </div>
        <div className="label-input-group">
          <div className="flex items-center">
            <DrugsAutoComplete
              appendTo={null}
              label={t('donorScheduling.fshDrug2')}
              defaultValue={workingDonorSchedule?.drug2 || undefined}
              field="name"
              disabled={viewOnly || isFieldLocked}
              onSelect={handleDrug2Change}
              className="w-full"
              tabIndex={2}
            />
            <div className="label-input-group">
              <label></label>
              {renderLockIcon()}
            </div>
          </div>
        </div>
        <div className="label-input-group">
          <div className="flex items-end gap-2">
            <div className="w-full flex-1">
              <ScheduleAutoComplete
                appendTo={null}
                label={
                  t('donorScheduling.scheduleMaster') +
                  (isRequiredFieldAsterisksEnabled ? '*' : '')
                }
                placeholder="Search Schedules"
                emptyMessage="No results found."
                defaultValue={workingDonorSchedule?.scheduleMaster || undefined}
                field="name"
                disabled={viewOnly}
                forceNewSuggestionsRef
                onKeyDown={(e) => {
                  if (
                    e.key === 'Enter' &&
                    !isScheduleMasterEnterEnabled &&
                    !isOpenCombobox(e.currentTarget)
                  ) {
                    e.preventDefault();
                    setShowScheduleDialog(true);
                  }
                }}
                onSelect={async (e: AutoCompleteSelectEvent) => {
                  dispatch({
                    type: DonorSchedulingActions.UPDATE_SCHEDULE_MASTER,
                    payload: {
                      scheduleMaster: e.value,
                      scheduleMasterId: e.value.id,
                    },
                  });
                  if (isNoStimSchedule(e.value.name)) {
                    dispatch({
                      type: DonorSchedulingActions.UPDATE_FSH_DRUG_1,
                      payload: { fshId: undefined, drug: null },
                    });
                    dispatch(
                      addToast({
                        severity: 'info',
                        summary: 'NO STIM Schedule',
                        detail:
                          'FSH Drug 1 cleared — NO STIM schedule selected',
                        life: ToastLife.DEFAULT,
                      }),
                    );
                  } else {
                    const currentDrug = workingDonorSchedule?.drug;
                    const currentDrugName =
                      typeof currentDrug === 'string'
                        ? currentDrug
                        : currentDrug?.name;
                    if (isFolltropinDefaultDrug(currentDrugName)) {
                      const drugName = getFolltropinDrugName(
                        e.value.twiceConcentration,
                      );
                      const fromLookup = findExactDrugByName(
                        fshDrugs,
                        drugName,
                      );
                      const resolved =
                        fromLookup ??
                        findExactDrugByName(
                          await searchDrugs({
                            search: drugName,
                            selectionLimit: SELECTION_LIMIT,
                          }).unwrap(),
                          drugName,
                        );
                      dispatch({
                        type: DonorSchedulingActions.UPDATE_FSH_DRUG_1,
                        payload: {
                          fshId: resolved?.id,
                          drug: resolved ?? drugName,
                          source: 'default',
                        },
                      });
                      dispatch(
                        addToast({
                          severity: 'warn',
                          summary: 'FSH Drug 1 Defaulted',
                          detail: `FSH Drug 1 set to ${drugName}`,
                          life: ToastLife.DEFAULT,
                        }),
                      );
                    } else {
                      dispatch(
                        addToast({
                          severity: 'info',
                          summary: 'FSH Drug 1 Retained',
                          detail:
                            'FSH Drug 1 kept — schedule has an existing drug saved',
                          life: ToastLife.DEFAULT,
                        }),
                      );
                    }
                  }
                }}
                tabIndex={3}
              />
            </div>
            <Button
              icon="pi pi-eye"
              disabled={viewOnly || !previousSchedulesV3?.length}
              tooltip="Select a previous schedule"
              onClick={() => setShowScheduleDialog(true)}
              className="mt-6"
            />
            <div className="label-input-group">
              <label></label>
              {renderLockIcon()}
            </div>
          </div>
        </div>
        <div className="label-input-group">
          <label>Bill Drugs</label>
          <SelectButton
            className="p-nucleus"
            value={workingDonorSchedule?.billDrugs}
            onChange={(e: DropdownChangeEvent) =>
              dispatch({
                type: DonorSchedulingActions.UPDATE_BILL_DRUGS,
                payload: e.value,
              })
            }
            options={TRUE_FALSE_SELECT_OPTIONS}
            optionLabel="name"
            optionValue="value"
            disabled={viewOnly}
            tabIndex={4}
          />
        </div>
        <div className="label-input-group">
          <label>{t('donorScheduling.soSchedule')}</label>
          <div className="flex w-full flex-row items-center gap-2">
            <InputText
              className="w-full"
              value={workingDonorSchedule?.soSchedule?.description || ''}
              disabled={true}
            />
            {isUpdateSoSchedulesEnabled &&
              hasSoSchedule &&
              (isSoScheduleEditLocked ? (
                // Past the aspiration date the SO can't be edited; the lock works like the
                // drug/master/target time locks (warns, then deactivates the SO schedule).
                renderLockIcon()
              ) : (
                <Button
                  type="button"
                  icon="pi pi-pencil"
                  className="p-button-text p-button-sm"
                  aria-label={t('donorScheduling.editSoSchedule.editButton')}
                  tooltip={t('donorScheduling.editSoSchedule.editButton')}
                  disabled={viewOnly}
                  onClick={() => setShowEditSoScheduleDialog(true)}
                />
              ))}
          </div>
        </div>
        {isScheduleSentEnabled && (
          <div className="label-input-group">
            <label>Schedule Sent</label>
            <SelectButton
              className="p-nucleus"
              value={
                workingDonorSchedule?.scheduled === true
                  ? true
                  : workingDonorSchedule?.scheduled === false
                    ? false
                    : null
              }
              onChange={(e: DropdownChangeEvent) =>
                dispatch({
                  type: DonorSchedulingActions.UPDATE_WORKING_DONOR_SCHEDULE,
                  payload: {
                    ...workingDonorSchedule,
                    scheduled: e.value ?? null,
                  },
                })
              }
              options={TRUE_FALSE_SELECT_OPTIONS}
              optionLabel="name"
              optionValue="value"
              disabled={viewOnly}
              tabIndex={5}
            />
          </div>
        )}
        <div className="label-input-group">
          <label>Clinic/Ranch/Haulin</label>
          <SelectButton
            className="p-nucleus"
            value={workingDonorSchedule?.clinicRanchHaulIn}
            onChange={(e: DropdownChangeEvent) =>
              dispatch({
                type: DonorSchedulingActions.UPDATE_CLINIC_RANCH_HAUL_IN,
                payload: e.value,
              })
            }
            options={CLINIC_RANCH_HAUL_IN_OPTIONS}
            optionLabel="name"
            optionValue="value"
            disabled={viewOnly}
            tabIndex={5 + scheduleSentTabOffset}
          />
        </div>
        <div className="label-input-group">
          <StringMapDropdown
            appendTo={undefined}
            label="Drug Shipping/Handling Fee"
            value={workingDonorSchedule?.drugShippingFee || undefined}
            objectTypeCode={StringMapObjectTypeCode.DRUG_SHIPPING_FEE}
            attributeName={StringMapAttributeName.DRUG_SHIPPING_FEE}
            onChange={(e: DropdownChangeEvent) =>
              dispatch({
                type: DonorSchedulingActions.UPDATE_DRUG_SHIPPING_FEE,
                payload: e.value,
              })
            }
            disabled={viewOnly}
            tabIndex={6 + scheduleSentTabOffset}
          />
        </div>
        <div className="label-input-group">
          <label>{t('donorScheduling.targetTime')}</label>

          <div className="flex w-full flex-row">
            <SelectButton
              className="w-full"
              value={workingDonorSchedule?.targetTime ?? true}
              onChange={handleTargetTimeChange}
              options={TARGET_TIME_OPTIONS}
              optionLabel="name"
              optionValue="value"
              disabled={viewOnly || isFieldLocked}
              tabIndex={7 + scheduleSentTabOffset}
            />

            {renderLockIcon()}
          </div>
        </div>
      </div>

      <ScheduleSelectionDialog
        visible={showScheduleDialog}
        onHide={handleScheduleCancel}
        schedules={(previousSchedulesV3 as Schedule[]) || []}
        selectedSchedule={selectedSchedule}
        onScheduleSelect={(schedule) => setSelectedSchedule(schedule)}
        onCancel={handleScheduleCancel}
      />

      {isUpdateSoSchedulesEnabled && hasSoSchedule && (
        <EditSoScheduleDialog
          visible={showEditSoScheduleDialog}
          soScheduleId={workingDonorSchedule?.soSchedule?.id}
          onHide={() => setShowEditSoScheduleDialog(false)}
          onSaved={handleSoScheduleSaved}
        />
      )}
    </div>
  );
};
